import { Value } from '../types';
import { getResolvedPathAndParameters } from '../util/url-util';

import authenticationService from './auth/authentication-service';
import { getContextName } from './configuration-service';

export enum HttpStatus {
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  MULTIPLE_CHOICES = 300,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
}

export enum HttpHeader {
  ACCEPT = 'Accept',
  AUTHORIZATION = 'Authorization',
  CONTENT_TYPE = 'Content-Type',
}

export enum MediaType {
  APPLICATION_JSON = 'application/json',
  APPLICATION_FORM_URLENCODED = 'application/x-www-form-urlencoded',
}

const DEFAULT_HEADERS = {
  [HttpHeader.ACCEPT]: MediaType.APPLICATION_JSON,
  [HttpHeader.CONTENT_TYPE]: MediaType.APPLICATION_JSON,
};

export interface ResponseBodyMapper<T> {
  map: (response: Response) => Promise<T>;
}

class BlobResponseBodyMapper implements ResponseBodyMapper<Blob | string> {

  map(response: Response): Promise<Blob | string> {
    return isStatusOk(response) ? response.blob() : response.text();
  }
}

class DefaultResponseBodyMapper implements ResponseBodyMapper<any> {

  map(response: Response): Promise<any> {
    return response.text().then((value: string) => {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    });
  }
}

export enum ResponseBodyMapperType {
  BLOB,
  DEFAULT,
}

const KNOWN_RESPONSE_BODY_MAPPERS: Map<ResponseBodyMapperType, ResponseBodyMapper<any>> = new Map([
  [ResponseBodyMapperType.DEFAULT, new DefaultResponseBodyMapper()],
  [ResponseBodyMapperType.BLOB, new BlobResponseBodyMapper()],
]);

export enum RequestBodyMapperType {
  BLOB = 'blob',
  DEFAULT = 'default',
}

const REQUEST_BODY_MAPPERS = {
  [RequestBodyMapperType.BLOB]: (body: object) => {
    const data = new FormData();

    for (const [key, value] of Object.entries(body)) {
      data.append(key, value);
    }

    return data;
  },
  [RequestBodyMapperType.DEFAULT]: (body: object) => JSON.stringify(body),
};

export class HttpError extends Error {
  response?: Response;
}

export interface RequestParameters {
  body?: BodyInit;
  headers: Headers;
  method: string;
  signal?: AbortSignal;
}

export interface RequestOptions {
  abortController?: AbortController;
  responseBodyMapperType?: ResponseBodyMapperType;
  requestBodyMapperType?: RequestBodyMapperType;
  body?: Record<string, Value>;
  headers?: Record<string, string>;
}

export interface RequestResponse<T = object> {
  body?: T;
  headers: Headers;
  status: number;
  statusText: string;
}

function chainRequest<T>(request: Request, responseBodyMapperType?: ResponseBodyMapperType): Promise<RequestResponse<T>> {
  applyAuthorizationHeader(request);

  const clonedRequest = request.clone();
  return fetch(request)
    .then(response => checkUnauthorized(clonedRequest, response))
    .then(response => verifyResponseAndGetBody(response, responseBodyMapperType));
}

function handleBodyAndGetPromise<T>(body: T, requestResponse: RequestResponse<T>): Promise<RequestResponse<T>> {
  requestResponse.body = body;
  return isStatusNumberOk(requestResponse.status)
    ? Promise.resolve(requestResponse)
    : Promise.reject(requestResponse);
}

function verifyResponseAndGetBody<T>(response: Response, responseBodyMapperType: ResponseBodyMapperType = ResponseBodyMapperType.DEFAULT): Promise<RequestResponse<T>> {
  const requestResponse: RequestResponse<T> = {
    headers: response.headers,
    status: response.status,
    statusText: response.statusText,
  };

  if (response.status === HttpStatus.NO_CONTENT) {
    return Promise.resolve(requestResponse);
  }

  const mapper = KNOWN_RESPONSE_BODY_MAPPERS.get(responseBodyMapperType);
  return mapper
    ? mapper.map(response).then((body: T) => handleBodyAndGetPromise(body, requestResponse))
    : Promise.reject('Unknown response body mapper');
}

function checkUnauthorized(request: Request, response: Response): Promise<Response> {
  if (response.status !== HttpStatus.UNAUTHORIZED) {
    return Promise.resolve(response);
  }

  return authenticationService
    .requestRefreshToken()
    .then(() => {
      applyAuthorizationHeader(request);
      return fetch(request);
    });
}

export function verifyResponseStatus(response: Response): Response {
  if (isStatusOk(response)) {
    return response;
  }

  const err = new HttpError(response.statusText);
  err.response = response;
  throw err;
}

export function isStatusOk(response: Response): boolean {
  return isStatusNumberOk(response.status);
}

function isStatusNumberOk(status: number): boolean {
  return status >= HttpStatus.OK && status < HttpStatus.MULTIPLE_CHOICES;
}

function applyAuthorizationHeader(request: Request): void {
  const token = authenticationService.getJWTToken();
  if (token) {
    request.headers.set(HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`);
  }
}

export function createURL(endpoint: string): URL {
  let url = endpoint;

  if (url.startsWith('/')) {
    const context = getContextName();

    if (context && !url.startsWith(context)) {
      url = `${context}/${url}`;
    }
    url = `/${url}`.replace(new RegExp('//+', 'g'), '/');
  }

  return new URL(url, window.location.origin);
}

function buildURL(endpoint: string, parameters: Record<string, Value> = {}): URL {
  if (!endpoint) throw new Error('Endpoint is not defined');

  const { queryParams, resolvedPath: solvedEndpoint } = getResolvedPathAndParameters(endpoint, parameters);

  const url = createURL(solvedEndpoint);
  const searchParams = url.searchParams;

  Object.entries(queryParams).forEach(([key, value]) => (
    Array.isArray(value)
      ? value.forEach(item => searchParams.append(key, item))
      : searchParams.append(key, String(value))
  ));

  return url;
}

function createRequestParameters(method: string, header: Record<string, string> = {}): RequestParameters {
  return { method, headers: new Headers(Object.assign(DEFAULT_HEADERS, header)) };
}

function doRequest<T>(url: URL, requestParameters: RequestParameters, abortController?: AbortController, responseBodyMapperType?: ResponseBodyMapperType): Promise<RequestResponse<T>> {
  if (abortController && (abortController instanceof AbortController)) {
    requestParameters.signal = abortController.signal;
  }

  const request = new Request(url.toString(), requestParameters);

  return chainRequest(request, responseBodyMapperType);
}

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE'
}

class HttpService {

  get<T>(endpoint: string, parameters: Record<string, Value> = {}, options?: RequestOptions): Promise<RequestResponse<T>> {
    return this.fetch(endpoint, HttpMethod.GET, parameters, options);
  }

  post<T>(endpoint: string, parameters: Record<string, Value> = {}, options?: RequestOptions): Promise<RequestResponse<T>> {
    return this.fetch(endpoint, HttpMethod.POST, parameters, options);
  }

  put<T>(endpoint: string, parameters: Record<string, Value> = {}, options?: RequestOptions): Promise<RequestResponse<T>> {
    return this.fetch(endpoint, HttpMethod.PUT, parameters, options);
  }

  delete<T>(endpoint: string, parameters: Record<string, Value> = {}, options?: RequestOptions): Promise<RequestResponse<T>> {
    return this.fetch(endpoint, HttpMethod.DELETE, parameters, options);
  }

  fetch<T>(endpoint: string, method: HttpMethod, parameters: Record<string, Value> = {}, options: RequestOptions = {}): Promise<RequestResponse<T>> {
    const url = buildURL(endpoint, parameters);
    const { body, headers, abortController, responseBodyMapperType } = options;
    const requestParameters = createRequestParameters(method as unknown as string, headers);

    if (body) {
      const mapperFn = REQUEST_BODY_MAPPERS[options.requestBodyMapperType ?? RequestBodyMapperType.DEFAULT];
      const mappedBody = mapperFn(body);

      requestParameters.body = mappedBody;
    }

    return doRequest(url, requestParameters, abortController, responseBodyMapperType);
  }
}

export default new HttpService();
